import type { WebContainer } from '@webcontainer/api';
import { persistUserFile } from '../runtime/tool-adapters/writeFile';
import { workbenchStore } from '../stores/workbench';
import { getHeadlessTemplateFiles } from '../templates/headlessTemplate';
import { materializeHotEditBridgeFilesInStore } from './mergedFiles';

const IDEoz_BRIDGE_PREFIX = 'src/ideoz/';

/**
 * Ensures snapshot restores (and legacy sandboxes) include the iframe preview bridge.
 * Idempotent; safe to call multiple times.
 */
export function patchMainTsxForBridge(content: string): string {
  if (content.includes('initPreviewBridge')) return content;

  const injectLines =
    '\nimport { initPreviewBridge } from "./ideoz/previewBridge";\ninitPreviewBridge();\n';

  const cssImport = /import\s+["']\.?\/?index\.css["'];?\r?\n/;
  if (cssImport.test(content)) return content.replace(cssImport, (m) => `${m}${injectLines}`);

  return `${injectLines}${content}`;
}

export async function ensureHotEditBridgeFiles(wc: WebContainer): Promise<void> {
  const templateFiles = getHeadlessTemplateFiles();

  for (const [relPath, body] of Object.entries(templateFiles)) {
    if (!relPath.startsWith(IDEoz_BRIDGE_PREFIX)) continue;
    let existing: string | undefined = workbenchStore.files.get()[relPath]?.content;
    if (existing === undefined) {
      try {
        existing = await wc.fs.readFile(relPath, 'utf-8');
      } catch {
        /* file missing — will write template */
      }
    }
    if (existing === body) continue;
    await persistUserFile(wc, relPath, body, false);
  }

  const mainPath = 'src/main.tsx';
  let mainContent = workbenchStore.files.get()[mainPath]?.content;
  if (mainContent === undefined) {
    try {
      mainContent = await wc.fs.readFile(mainPath, 'utf-8');
    } catch {
      mainContent = templateFiles[mainPath];
    }
  }
  if (!mainContent) {
    materializeHotEditBridgeFilesInStore();
    return;
  }

  const nextMain = patchMainTsxForBridge(mainContent);
  if (nextMain !== mainContent) {
    await persistUserFile(wc, mainPath, nextMain, false);
  }

  materializeHotEditBridgeFilesInStore();
}
